import type {
  EmailBuilderPerkItem,
  EmailBuilderPerkSocialKey,
} from "./emailBuilderData";
import { normalizeEmailLinkUrl } from "./emailSafeLink";
import { gmailKeepBackground, gmailKeepDarkText, gmailKeepLightText } from "./emailGmailDarkMode";
import { formatMemberPhoneNumberForDisplay } from "../../../utils/memberContactFormatting";

/**
 * Design (19) "Auto Select a Perk of the Week": a fixed gold banner, the
 * Chamber Perks App column (QR code, app logo, store badges) and a phone
 * showing the perk as the app does. Every image is a hosted PNG (email
 * clients drop SVG and data images) served by the dashboard deploy from
 * public/email-assets. The phone is a table: a top image (frame, status bar,
 * favourite/share row), 8 px dark side cells around the perk details, and a
 * bottom image (disclaimer, Redeem Later / Redeem, frame), so Outlook keeps it.
 */
export const PERK_WEEK_GOLD = "#E1A731";
export const EMAIL_ASSET_BASE_URL = "https://dashboard.chambermemberpro.com/email-assets";
export const CHAMBER_PERKS_APP_STORE_URL = "https://apps.apple.com/ca/app/chamber-perks/id1590497127";
export const CHAMBER_PERKS_GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.capappsudbury&hl=en_CA";

const asset = (file: string): string => `${EMAIL_ASSET_BASE_URL}/${file}`;

export const PERK_WEEK_ASSETS = {
  qr: asset("chamber-perks-qr.png"),
  appLogo: asset("chamber-perks-app-logo.png"),
  appStore: asset("app-store-badge.png"),
  googlePlay: asset("google-play-badge.png"),
  phoneTop: asset("perk-week-phone-top.png"),
  phoneBottom: asset("perk-week-phone-bottom.png"),
} as const;

/** Design PERK_SOCIAL_ORDER. */
export const PERK_SOCIAL_ORDER: EmailBuilderPerkSocialKey[] = [
  "website", "email", "phone", "google", "instagram", "facebook", "linkedin", "x", "tiktok",
];
const PERK_SOCIAL_TITLES: Record<EmailBuilderPerkSocialKey, string> = {
  website: "Website", email: "Email", phone: "Phone", google: "Google Reviews", instagram: "Instagram",
  facebook: "Facebook", linkedin: "LinkedIn", x: "X", tiktok: "TikTok",
};

type PerkDetailKey = "details" | "availability" | "howto" | "contact" | "phone" | "email";

// Chamber Perks App wording (capApp RedeemableDiscountModal).
export const PERK_AVAILABILITY_TEXT = {
  multiple: "This perk can be redeemed multiple times. Tap redeem each time you use this perk.",
  once: "This perk can only be redeemed once. Tap redeem if you redeemed this perk.",
} as const;
export const PERK_HOW_TO_REDEEM_TEXT: Record<string, string> = {
  CONTACT_COMPANY: "Contact company and mention this perk.",
  IN_PERSON: "Visit company and show this screen at time of purchase.",
  ONLINE: "Visit company website and enter perk code at checkout.",
};

/** The fields GetCmpEmailBuilderPerks returns for the Perk of the Week phone. */
export type PerkWeekDiscountDetails = {
  isReoccuring?: boolean | null;
  redeemSettings: {
    whereToRedeem: string;
    howToRedeem?: { contactName?: string | null; contactNumber?: string | null; contactEmail?: string | null } | null;
  };
  chamberMemberInfo: {
    website?: string | null; email?: string | null; phoneNumber?: string | null; googleReviewLink?: string | null;
    instagram?: string | null; facebook?: string | null; linkedin?: string | null; twitter?: string | null; tiktok?: string | null;
  };
};

/**
 * The phone's detail rows and social icons, by the app's own rules: "To
 * Redeem Perk" shows when the perk has redeem instructions, the contact rows
 * only for Contact Company perks, and an icon for each member link the app
 * shows that the design has an icon for.
 */
export const getPerkWeekDetailFields = (
  perk: PerkWeekDiscountDetails
): Pick<EmailBuilderPerkItem, "availability" | "howToRedeem" | "contactName" | "contactPhone" | "contactEmail" | "socials"> => {
  const how = perk.redeemSettings.howToRedeem;
  const contactCompany = perk.redeemSettings.whereToRedeem === "CONTACT_COMPANY";
  const member = perk.chamberMemberInfo;
  const links: Record<EmailBuilderPerkSocialKey, string | null | undefined> = {
    website: member.website, email: member.email, phone: member.phoneNumber, google: member.googleReviewLink,
    instagram: member.instagram, facebook: member.facebook, linkedin: member.linkedin, x: member.twitter, tiktok: member.tiktok,
  };
  return {
    availability: typeof perk.isReoccuring === "boolean"
      ? perk.isReoccuring ? PERK_AVAILABILITY_TEXT.multiple : PERK_AVAILABILITY_TEXT.once
      : "",
    howToRedeem: how ? PERK_HOW_TO_REDEEM_TEXT[perk.redeemSettings.whereToRedeem] || "" : "",
    contactName: contactCompany ? (how?.contactName || "").trim() : "",
    contactPhone: contactCompany ? formatMemberPhoneNumberForDisplay(how?.contactNumber).trim() : "",
    contactEmail: contactCompany ? (how?.contactEmail || "").trim() : "",
    socials: PERK_SOCIAL_ORDER.filter((key) => !!(links[key] || "").trim()),
  };
};

// Design DEFAULTS.perkWeek placeholder perk (canvas and palette only).
const placeholderPerk: EmailBuilderPerkItem = {
  id: "", title: "Perk Title", company: "Member Company Name", logoUrl: "",
  description: "The perk description will show here once a perk is selected.",
  redeem: "In Person", validUntil: "Valid Until Date", createdAtUtcMilli: null,
  socials: ["website", "email", "phone", "instagram", "facebook"],
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const linked = (content: string, url: string, style = ""): string => {
  const safeUrl = normalizeEmailLinkUrl(url);
  return safeUrl
    ? `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;${style}">${content}</a>`
    : content;
};

// Narrow screens (the design wraps below 458 px): the phone sits above the
// app column, the subtitle pill may wrap. Media queries cover delivered
// email, container queries the builder canvas.
const PERK_WEEK_NARROW_MAX_WIDTH = 457;
const perkWeekNarrowCss = [
  ".cmp-perk-week-columns,.cmp-perk-week-columns>tbody{display:block!important;width:100%!important;}",
  ".cmp-perk-week-columns>tbody>tr{display:table!important;width:100%!important;}",
  ".cmp-perk-week-columns td.cmp-perk-week-phone{display:table-header-group!important;width:100%!important;}",
  ".cmp-perk-week-columns td.cmp-perk-week-app{display:table-footer-group!important;width:100%!important;}",
  ".cmp-perk-week-app-inner{padding-top:16px!important;}",
  ".cmp-perk-week-phone-inner{padding-left:0!important;}",
  ".cmp-perk-week-pill{white-space:normal!important;}",
  ".cmp-perk-week-pill-side{width:12px!important;}",
  // EM-3H-15: "PERK OF THE WEEK" stays on one line on a 375 px phone.
  ".cmp-perk-week-label{font-size:24px!important;letter-spacing:0!important;}",
].join("");
const perkWeekNarrowStyle = `<style>@media only screen and (max-width:${PERK_WEEK_NARROW_MAX_WIDTH}px){${perkWeekNarrowCss}}@container (max-width:${PERK_WEEK_NARROW_MAX_WIDTH}px){${perkWeekNarrowCss}}</style>`;

const PHONE_WIDTH = 220;
const PHONE_TOP_HEIGHT = 63;
const PHONE_BOTTOM_HEIGHT = 82;
// Design phone: aspect-ratio 390/844 at 220 px wide = 476 px, overflow
// hidden. The phone keeps that height (decision B16); a long perk is cut off.
const PHONE_MIDDLE_HEIGHT = 476 - PHONE_TOP_HEIGHT - PHONE_BOTTOM_HEIGHT;
const PHONE_LOGO_ROW_HEIGHT = 58;
// "Connect with" line and one row of social icons.
const PHONE_CONNECT_ROW_HEIGHT = 56;
// The perk name and detail rows share the rest; extra lines are hidden.
const PHONE_DETAILS_MAX_HEIGHT = PHONE_MIDDLE_HEIGHT - PHONE_LOGO_ROW_HEIGHT - PHONE_CONNECT_ROW_HEIGHT;
// Clients that ignore max-height (Outlook for Windows) get shorter text.
const PHONE_TEXT_MAX_LENGTH = 160;
const clampPhoneText = (text: string): string =>
  text.length > PHONE_TEXT_MAX_LENGTH ? `${text.slice(0, PHONE_TEXT_MAX_LENGTH - 1).trimEnd()}\u2026` : text;

const spacerCell = (attributes: string, style: string): string =>
  `<td${attributes} style="${style}font-size:0;line-height:0;mso-line-height-rule:exactly;">&nbsp;</td>`;

const renderBanner = (labelHtml: string, subtitleHtml: string): string => {
  const gold = `bgcolor="${PERK_WEEK_GOLD}"`;
  const label = `<tr><td colspan="3" ${gold} style="background:${PERK_WEEK_GOLD};${gmailKeepBackground(PERK_WEEK_GOLD)}padding:14px 18px ${subtitleHtml ? 8 : 22}px;text-align:center;"><span class="cmp-perk-week-label" style="font-size:30px;font-weight:800;letter-spacing:.01em;color:#FFFFFF;line-height:1.15;text-transform:uppercase;">${gmailKeepLightText(labelHtml)}</span></td></tr>`;
  // The pill overlaps the banner by 14 px: it spans two rows whose side
  // cells are gold (top 14 px) and white (below), so no negative margin.
  const side = (goldBand: boolean) => spacerCell(
    ` class="cmp-perk-week-pill-side" width="50%"${goldBand ? ` ${gold}` : ""}`,
    `width:50%;height:${goldBand ? 14 : 21}px;${goldBand ? `background:${PERK_WEEK_GOLD};${gmailKeepBackground(PERK_WEEK_GOLD)}` : ""}`,
  );
  const pill = `<td class="cmp-perk-week-pill" rowspan="2" ${gold} style="background:${PERK_WEEK_GOLD};${gmailKeepBackground(PERK_WEEK_GOLD)}border:3px solid #FFFFFF;border-radius:999px;padding:5px 26px;font-size:15px;font-weight:700;line-height:1.3;color:#FFFFFF;text-align:center;white-space:nowrap;">${gmailKeepLightText(subtitleHtml)}</td>`;
  const rows = subtitleHtml ? `${label}<tr>${side(true)}${pill}${side(true)}</tr><tr>${side(false)}${side(false)}</tr>` : label;
  // Design: the banner sits 20 px in from each side.
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr><td style="padding:0 20px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:separate;border-spacing:0;">${rows}</table></td></tr></table>`;
};

const renderAppColumn = (): string => {
  const badge = (src: string, alt: string, url: string, marginTop: number) =>
    `<div style="margin-top:${marginTop}px;max-width:180px;">${linked(`<img src="${src}" alt="${alt}" width="180" style="display:block;width:100%;max-width:180px;height:auto;border:0;outline:none;" />`, url, "display:block;")}</div>`;
  return `<div class="cmp-perk-week-app-inner" style="text-align:left;">`
    + `<img src="${PERK_WEEK_ASSETS.qr}" alt="Scan to download the Chamber Perks app" width="120" style="display:block;width:100%;max-width:120px;height:auto;border:0;outline:none;" />`
    + `<img src="${PERK_WEEK_ASSETS.appLogo}" alt="Chamber Perks Mobile App" width="180" style="display:block;width:100%;max-width:180px;height:auto;margin-top:8px;border:0;outline:none;" />`
    + badge(PERK_WEEK_ASSETS.appStore, "Download on the App Store", CHAMBER_PERKS_APP_STORE_URL, 8)
    + badge(PERK_WEEK_ASSETS.googlePlay, "Get it on Google Play", CHAMBER_PERKS_GOOGLE_PLAY_URL, 6)
    + `</div>`;
};

// The phone's logo row. A real perk without a member logo has no logo row
// at all (Moeen rule, Oct 19 2026: never copy the design "Member Logo"
// placeholder into the canvas, preview or sent email); the perk text uses
// the space. Only the canvas and palette placeholder perk (no perk selected,
// never sent) shows the design placeholder box.
const renderPhoneLogo = (perk: EmailBuilderPerkItem, placeholder: boolean): string => {
  if (perk.logoUrl)
    return `<img src="${escapeHtml(perk.logoUrl)}" alt="${escapeHtml(perk.company)} logo" width="120" style="display:inline-block;width:auto;max-width:120px;max-height:56px;height:auto;object-fit:contain;vertical-align:middle;border:0;" />`;
  return placeholder
    ? `<div style="display:inline-block;box-sizing:border-box;width:120px;height:52px;border-radius:10px;background:#F1F1F3;color:#727272;text-align:center;vertical-align:middle;line-height:50px;border:1px dashed #9E9E9E;font-size:10px;font-weight:600;">Member Logo</div>`
    : "";
};

const detailRow = (key: PerkDetailKey, title: string, text: string, underline = false): string =>
  `<tr><td style="padding:8px 6px 0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr>`
  + `<td width="16" style="width:16px;vertical-align:top;padding-top:1px;"><img src="${asset(`perk-week-detail-${key}.png`)}" alt="" width="16" height="16" style="display:block;width:16px;height:16px;border:0;" /></td>`
  + `<td style="vertical-align:top;padding-left:8px;font-size:9px;line-height:1.35;color:#0A0A0A;text-align:left;">${gmailKeepDarkText(`<div style="font-size:10px;font-weight:700;">${title}</div><div style="overflow-wrap:anywhere;word-break:break-word;${underline ? "text-decoration:underline;" : ""}">${escapeHtml(text)}</div>`)}</td>`
  + `</tr></table></td></tr>`;

const renderPhone = (perk: EmailBuilderPerkItem, linkUrl: string, placeholder: boolean): string => {
  const logo = renderPhoneLogo(perk, placeholder);
  const rows: Array<[PerkDetailKey, string, string | undefined, boolean?]> = [
    ["details", "Perk Details", perk.description],
    ["availability", "Redemption Availability", perk.availability],
    ["howto", "To Redeem Perk", perk.howToRedeem],
    ["contact", "Contact Name", perk.contactName],
    ["phone", "Phone", perk.contactPhone, true],
    ["email", "Email", perk.contactEmail, true],
  ];
  const details = rows
    .filter(([, , text]) => !!(text || "").trim())
    .map(([key, title, text, underline]) => detailRow(key, title, clampPhoneText((text || "").trim()), underline))
    .join("");
  const socials = (perk.socials || [])
    .filter((key) => PERK_SOCIAL_ORDER.includes(key))
    .sort((a, b) => PERK_SOCIAL_ORDER.indexOf(a) - PERK_SOCIAL_ORDER.indexOf(b))
    .map((key) => `<img src="${asset(`perk-week-social-${key}.png`)}" alt="${PERK_SOCIAL_TITLES[key]}" title="${PERK_SOCIAL_TITLES[key]}" width="18" height="18" style="display:inline-block;width:18px;height:18px;margin:0 1.5px 3px;border:0;vertical-align:middle;" />`)
    .join("");
  const company = escapeHtml(perk.company);
  const nameAndDetails = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">`
    + `<tr><td style="padding:8px 6px 0;text-align:left;color:#0A0A0A;">${gmailKeepDarkText(`<div style="font-size:10px;">${company}</div><div style="margin-top:1px;font-size:14px;font-weight:700;line-height:1.2;">${linked(escapeHtml(perk.title), linkUrl, "color:#0A0A0A;")}</div>`)}</td></tr>`
    + details
    + `</table>`;
  const content = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" height="${PHONE_MIDDLE_HEIGHT}" style="border-collapse:collapse;height:${PHONE_MIDDLE_HEIGHT}px;table-layout:fixed;">`
    + (logo ? `<tr><td align="center" valign="middle" style="height:${PHONE_LOGO_ROW_HEIGHT}px;text-align:center;vertical-align:middle;font-size:0;line-height:0;">${logo}</td></tr>` : "")
    + `<tr><td valign="top" style="vertical-align:top;"><div class="cmp-perk-week-phone-details" style="max-height:${logo ? PHONE_DETAILS_MAX_HEIGHT : PHONE_DETAILS_MAX_HEIGHT + PHONE_LOGO_ROW_HEIGHT}px;overflow:hidden;">${nameAndDetails}</div></td></tr>`
    + `<tr><td valign="bottom" style="height:${PHONE_CONNECT_ROW_HEIGHT}px;vertical-align:bottom;text-align:center;">${gmailKeepDarkText(`<div style="font-size:10px;color:#0A0A0A;text-decoration:underline;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">Connect with ${company}</div>`)}${socials ? `<div style="margin-top:7px;font-size:0;line-height:0;max-height:21px;overflow:hidden;">${socials}</div>` : ""}</td></tr>`
    + `</table>`;
  const frameImage = (src: string, height: number, alt: string) =>
    `<tr><td colspan="3" style="font-size:0;line-height:0;">${linked(`<img src="${src}" alt="${alt}" width="${PHONE_WIDTH}" height="${height}" style="display:block;width:${PHONE_WIDTH}px;height:${height}px;border:0;outline:none;" />`, linkUrl, "display:block;")}</td></tr>`;
  // Gmail dark mode keeps the frame dark and the screen white, as in the frame images.
  const side = spacerCell(' width="8" bgcolor="#1A1A1A"', `width:8px;background:#1A1A1A;${gmailKeepBackground("#1A1A1A")}`);
  return `<table role="presentation" align="center" width="${PHONE_WIDTH}" cellpadding="0" cellspacing="0" style="width:${PHONE_WIDTH}px;margin:0 auto;border-collapse:collapse;">`
    + frameImage(PERK_WEEK_ASSETS.phoneTop, PHONE_TOP_HEIGHT, "")
    + `<tr>${side}<td width="204" bgcolor="#FFFFFF" style="width:204px;background:#FFFFFF;${gmailKeepBackground("#FFFFFF")}vertical-align:top;padding:0 10px;">${content}</td>${side}</tr>`
    + frameImage(PERK_WEEK_ASSETS.phoneBottom, PHONE_BOTTOM_HEIGHT, "This offer cannot be combined with any other offer. May require additional verification. Redeem Later. Redeem.")
    + `</table>`;
};

export type PerkOfTheWeekRenderInput = {
  perk: EmailBuilderPerkItem | null;
  linkUrl: string;
  /** Already rich-text rendered; "" = hidden or empty. */
  labelHtml: string;
  subtitleHtml: string;
  headlineHtml: string;
  /** Hide on HEADLINE: no headline, even when a perk is selected. */
  headlineHidden: boolean;
  /** Builder canvas and palette: the design placeholder perk when none is selected. */
  placeholder: boolean;
  /** Palette thumbnail: the design's narrow layout (phone above the app column). */
  stacked?: boolean;
};

/** Content for the block's section shell (padding 20px 0 24px). */
export const renderPerkOfTheWeekContent = ({
  perk, linkUrl, labelHtml, subtitleHtml, headlineHtml, headlineHidden, placeholder, stacked = false,
}: PerkOfTheWeekRenderInput): string => {
  const shownPerk = perk || (placeholder ? placeholderPerk : null);
  // Design: the headline falls back to the perk title.
  const headline = headlineHtml || (headlineHidden || !shownPerk ? "" : escapeHtml(shownPerk.title));
  const phone = shownPerk ? renderPhone(shownPerk, linkUrl, !perk) : "";
  const app = renderAppColumn();
  const columns = stacked
    ? `<div>${phone}</div><div style="padding-top:16px;">${app}</div>`
    : phone
      ? `<table class="cmp-perk-week-columns" role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;table-layout:fixed;width:100%;"><tbody><tr>`
        + `<td class="cmp-perk-week-app" valign="bottom" style="vertical-align:bottom;padding-bottom:6px;text-align:left;">${app}</td>`
        + `<td class="cmp-perk-week-phone" width="244" valign="middle" style="width:244px;vertical-align:middle;"><div class="cmp-perk-week-phone-inner" style="padding-left:24px;">${phone}</div></td>`
        + `</tr></tbody></table>`
      : app;
  return `${perkWeekNarrowStyle}<div style="text-align:center;">${renderBanner(labelHtml, subtitleHtml)}`
    + (headline ? `<div style="padding:18px 28px 0;font-size:24px;font-weight:800;color:#0A0A0A;line-height:1.3;text-align:center;">${headline}</div>` : "")
    + `<div style="padding:18px 24px 0;text-align:left;">${columns}</div></div>`;
};

/** Plain-text lines for the perk (the email's text part). */
export const getPerkOfTheWeekTextLines = (perk: EmailBuilderPerkItem | null): string[] =>
  perk
    ? [perk.company, perk.title, perk.description, perk.availability || "", perk.howToRedeem || "", perk.contactName || "", perk.contactPhone || "", perk.contactEmail || ""]
    : [];
